import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import type { GlobalBasicSearchQueryInterface } from '@zaoblako/shared/common/dtos/search/global.basic.search.query.dto';
import type { GlobalBasicSearchResultInterface } from '@zaoblako/shared/common/dtos/search/global.basic.search.result.dto';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { SearchService } from '@zaoblako/web/core/services/search.service';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { HighlightPipe } from '@zaoblako/web/shared/pipes/highlight.pipe';
import { SafeHtmlPipe } from '@zaoblako/web/shared/pipes/safe-html.pipe';
import { SearchTruncatePipe } from '@zaoblako/web/shared/pipes/search-truncate.pipe';
import { DialogModule } from 'primeng/dialog';
import { ListboxModule } from 'primeng/listbox';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { TooltipModule } from 'primeng/tooltip';
import { Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { Key } from 'ts-key-enum';

@Component({
  standalone: true,
  selector: 'crm-global-search',
  templateUrl: './global-search.component.html',
  styleUrls: ['./global-search.component.scss'],
  imports: [
    CommonModule,
    DialogModule,
    OverlayPanelModule,
    ListboxModule,
    InputTextComponent,
    FormsModule,
    TooltipModule,
    HighlightPipe,
    SafeHtmlPipe,
    SearchTruncatePipe,
    RouterModule
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GlobalSearchComponent implements OnInit, OnDestroy {
  /** Ширина выпадашки результатов над нижней панелью (шире «пилюли» поиска). */
  private static readonly bottomBarResultsPanelPreferredWidthPx = 520;

  /** Вариант оформления для нижней панели (rental_rate_report_v2 / FRM 2.0). */
  @Input() public toolbarLayout: 'default' | 'bottomBar' = 'default';

  @HostBinding('class.gs-host--bottom-bar')
  public get isBottomBarHost(): boolean {
    return this.toolbarLayout === 'bottomBar';
  }

  /** Fixed-позиция выпадашки над нижней панелью (у `.bb` overflow-y: hidden). */
  public desktopResultsPanelStyle: Record<string, string> = {};

  /** Плейсхолдер поля поиска (в т.ч. «Быстрый поиск…» в нижней панели). */
  @Input() public searchPlaceholder = 'Поиск';

  /** Подпись шортката фокуса поиска (без вызова функций в шаблоне). */
  public readonly shortcutOpenSearch: string =
    typeof navigator !== 'undefined' &&
    /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent || navigator.platform || '')
      ? '⌘K'
      : 'Ctrl+K';

  @ViewChild('searchInput', { static: false }) public searchInput?: InputTextComponent;
  @ViewChild('mobileSearchInput', { static: false }) public mobileSearchInput?: InputTextComponent;
  @ViewChild('searchWindow', { static: false }) public searchWindow: ElementRef;

  /** В нижней полосе — как у информеров: подсветка кнопки, пока открыты настройки фильтра. */
  public filterSettingsOpen = false;

  /** Классы p-overlayPanel: в bottomBar — те же, что у crm-notification-list (crm-theme · informer-overlay-panel-v2). */
  public get filterSettingsOverlayStyleClass(): string {
    const marker = ' gs-global-search-filter-overlay';
    return this.toolbarLayout === 'bottomBar'
      ? `dropdown-overlay-panel informer-overlay-panel-v2${marker}`
      : `dropdown-overlay-panel${marker}`;
  }

  /** Совпадает с порогом `crm-action-bar` (<576px): поле поиска в нижней полосе не помещается. */
  public isCompactMobile = false;

  public mobileSearchOpen = false;

  public loading = false;
  public search = new Subject<string>();
  public searchResults$: Observable<GlobalBasicSearchResultInterface>;

  public queryParams: GlobalBasicSearchQueryInterface = {
    deal: false,
    project: false,
    hd: false,
    task: false,
    user: false,
    company: false,
    contragent: false,
    q: ''
  };

  public readonly filterSearchInfo: { name: string; label: string; accessName: string }[] = [
    { name: 'deal', label: 'Сделки', accessName: 'FINANCE' },
    { name: 'project', label: 'Задачи', accessName: 'PROJECT' },
    { name: 'hd', label: 'HelpDesk', accessName: 'HELPDESK' },
    { name: 'task', label: 'Платежи', accessName: 'FINANCE' },
    { name: 'user', label: 'Пользователи', accessName: 'BASIS' },
    { name: 'company', label: 'Компании', accessName: 'BASIS' },
    { name: 'contragent', label: 'Контрагенты', accessName: 'BASIS' }
  ];

  public filterSearch: { name: string; label: string; accessName: string }[] = [];

  public selectedFilterSearch: { name: string; label: string; accessName: string }[] = [];

  private readonly mobileMediaQuery = '(max-width: 575px)';

  private mediaQueryList?: MediaQueryList;

  private readonly onMediaQueryChangeBound = (): void => {
    this.syncCompactMobileFromMedia();
  };

  private readonly onWindowResizeBound = (): void => {
    this.syncDesktopResultsPanelPosition();
  };

  private readonly onDocumentClickBound = (event: MouseEvent): void => {
    const target = event.target as Node | null;
    if (!target || this.mobileSearchOpen) {
      return;
    }
    if (this.searchWindow?.nativeElement?.contains(target)) {
      return;
    }
    if (this.hostRef.nativeElement.contains(target)) {
      return;
    }
    this.search.next('');
    const input = this.searchInput?.input?.nativeElement as HTMLInputElement | undefined;
    input?.blur();
  };

  public constructor(
    private readonly searchService: SearchService,
    private readonly globalService: GlobalService,
    private readonly cdRef: ChangeDetectorRef,
    /** Корень `crm-global-search` — клики внутри не должны закрывать поиск и снимать фокус с поля. */
    private readonly hostRef: ElementRef<HTMLElement>
  ) {}

  public ngOnInit(): void {
    this.globalService
      .select((state) => state.tenantInfo)
      .subscribe((tenantInfo) => {
        this.filterSearch = [];
        this.selectedFilterSearch = [];

        const modules = Object
          .entries(tenantInfo?.ratePlan?.modulesAccessControl || {})
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          .filter(([_, module]) => module.enabled)
          .map(([name]) => name)
        this.initQueryParams(modules);

        const listAccessName: string[] = modules;

        this.filterSearchInfo.forEach((item) => {
          if (listAccessName.includes(item.accessName)) {
            this.filterSearch.push(item);

            if (this.queryParams[item.name]) {
              this.selectedFilterSearch.push(item);
            }
          }
        });
        this.cdRef.markForCheck();
      });

    this.searchResults$ = this.search.pipe(
      map((query) => ({
        q: (query ?? this.queryParams.q ?? '').trim(),
        filterKey: this.buildSearchFilterKey()
      })),
      debounceTime(300),
      distinctUntilChanged((prev, curr) => prev.q === curr.q && prev.filterKey === curr.filterKey),
      switchMap(({ q }) => {
        if (!q || q.length < 2) {
          this.loading = false;
          this.cdRef.markForCheck();
          return of(null);
        }
        this.loading = true;
        this.cdRef.markForCheck();
        return this.searchService.search({ ...this.queryParams, q }).pipe(
          tap((body) => {
            if (body) {
              queueMicrotask(() => {
                this.syncDesktopResultsPanelPosition();
              });
            }
          }),
          catchError((err: unknown) => {
            if (this.isCanceledSearchRequest(err)) {
              return of(null);
            }
            return of(null);
          }),
          finalize(() => {
            this.loading = false;
            this.cdRef.markForCheck();
          })
        );
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    document.addEventListener('click', this.onDocumentClickBound);

    this.mediaQueryList = window.matchMedia(this.mobileMediaQuery);
    this.syncCompactMobileFromMedia();
    this.mediaQueryList.addEventListener('change', this.onMediaQueryChangeBound);
    window.addEventListener('resize', this.onWindowResizeBound);
    window.addEventListener('scroll', this.onWindowResizeBound, true);
  }

  public ngOnDestroy(): void {
    document.removeEventListener('click', this.onDocumentClickBound);
    this.mediaQueryList?.removeEventListener('change', this.onMediaQueryChangeBound);
    window.removeEventListener('resize', this.onWindowResizeBound);
    window.removeEventListener('scroll', this.onWindowResizeBound, true);
  }

  /** Как `onInformerShow` / `onInformerHide` у информеров — только подсветка триггера. */
  public onFilterSettingsOverlayShow(): void {
    if (this.toolbarLayout === 'bottomBar') {
      this.filterSettingsOpen = true;
    }
    this.cdRef.markForCheck();
  }

  public onFilterSettingsOverlayHide(): void {
    this.filterSettingsOpen = false;
    this.cdRef.markForCheck();
  }

  public changeFilterSearch(selectedFilter: { name: string; label: string; accessName: string }[]) {
    this.filterSearch.forEach((item) => {
      this.queryParams[item.name] = selectedFilter.findIndex((f) => f.name === item.name) !== -1;
      localStorage.setItem(`global.search.${item.name}`, String(this.queryParams[item.name]));
    });
    this.search.next(this.queryParams.q);
  }

  public closeSearchWindow() {
    this.search.next('');
    if (this.isCompactMobile) {
      this.mobileSearchOpen = false;
      this.cdRef.markForCheck();
    }
  }

  /** Клик по оболочке нижней панели (отступ вокруг input) — фокус в поле. */
  public onDesktopSearchShellClick(event: MouseEvent): void {
    if (this.toolbarLayout !== 'bottomBar' || this.isCompactMobile) {
      return;
    }
    const el = event.target as HTMLElement | null;
    if (!el) {
      return;
    }
    if (el.closest('input') || el.closest('.frm-input-text-clear-icon')) {
      return;
    }
    this.focusQueryField();
  }

  /** Фокус поля запроса (глобальный шорткат из нижней панели). */
  public focusQueryField(): void {
    if (this.isCompactMobile) {
      this.mobileSearchOpen = true;
      this.cdRef.markForCheck();
      return;
    }
    const el = this.searchInput?.input?.nativeElement as HTMLInputElement | undefined;
    el?.focus();
    queueMicrotask(() => {
      this.syncDesktopResultsPanelPosition();
    });
  }

  public openMobileSearchSheet(): void {
    this.mobileSearchOpen = true;
    this.cdRef.markForCheck();
  }

  public onMobileSearchDialogShown(): void {
    queueMicrotask(() => {
      const el = this.mobileSearchInput?.input?.nativeElement as HTMLInputElement | undefined;
      el?.focus();
      this.search.next(this.queryParams.q);
      this.cdRef.markForCheck();
    });
  }

  public onMobileSearchDialogHide(): void {
    const el = this.mobileSearchInput?.input?.nativeElement as HTMLInputElement | undefined;
    el?.blur();
  }

  @HostListener('document:keydown', ['$event'])
  public onKeyDown(event: KeyboardEvent) {
    if (event.key === Key.Escape) {
      if (this.mobileSearchOpen) {
        this.mobileSearchOpen = false;
        this.cdRef.markForCheck();
        return;
      }
      this.search.next('');
      (this.searchInput?.input?.nativeElement as HTMLInputElement | undefined)?.blur();
    }
  }

  private syncCompactMobileFromMedia(): void {
    const next = this.mediaQueryList?.matches ?? window.matchMedia(this.mobileMediaQuery).matches;
    if (next === this.isCompactMobile) {
      return;
    }
    this.isCompactMobile = next;
    if (!next) {
      this.mobileSearchOpen = false;
    }
    this.cdRef.markForCheck();
  }

  private initQueryParams(modules: string[]) {
    this.filterSearchInfo.forEach((item) => {
      if (modules.includes(item.accessName)) {
        this.queryParams[item.name] = localStorage.getItem(`global.search.${item.name}`)
          ? localStorage.getItem(`global.search.${item.name}`) === 'true'
          : true;
      } else {
        this.queryParams[item.name] = false;
      }
    });
  }

  private buildSearchFilterKey = (): string => {
    return this.filterSearchInfo
      .map((item) => `${item.name}:${String(this.queryParams[item.name])}`)
      .join(',');
  };

  /** Отмена in-flight запроса (switchMap) — не считать ошибкой поиска. */
  private isCanceledSearchRequest = (err: unknown): boolean => {
    if (!(err instanceof HttpErrorResponse)) {
      return false;
    }
    return err.status === 0 && navigator.onLine;
  };

  private syncDesktopResultsPanelPosition = (): void => {
    if (this.toolbarLayout !== 'bottomBar' || this.isCompactMobile) {
      this.desktopResultsPanelStyle = {};
      this.cdRef.markForCheck();
      return;
    }
    const anchor = this.searchInput?.input?.nativeElement as HTMLElement | undefined;
    const anchorRect = anchor?.getBoundingClientRect();
    if (!anchorRect) {
      return;
    }
    const viewportMarginPx = 12;
    const panelWidth = Math.min(
      GlobalSearchComponent.bottomBarResultsPanelPreferredWidthPx,
      window.innerWidth - viewportMarginPx * 2
    );
    let panelLeftPx = anchorRect.left;
    if (panelLeftPx + panelWidth > window.innerWidth - viewportMarginPx) {
      panelLeftPx = Math.max(viewportMarginPx, window.innerWidth - viewportMarginPx - panelWidth);
    }
    this.desktopResultsPanelStyle = {
      position: 'fixed',
      left: `${panelLeftPx}px`,
      width: `${panelWidth}px`,
      top: `${anchorRect.top - 8}px`,
      transform: 'translateY(-100%)',
      'margin-left': '0',
      'z-index': '10002'
    };
    this.cdRef.markForCheck();
  };
}
